/**
 * styleConstants.js
 * Central file for styling constants across the application
 */

// Color palette based on the designs
export const colors = {
  // Primary colors
  darkSlateBlue: '#3D485A',  // Primary Background
  mediumSlateBlue: '#5E6C84', // Accent Background
  actionBlue: '#3B82F6',     // Buttons & Links
  
  // Secondary colors
  softWhite: '#F8F9FA',      // Card Backgrounds
  charcoal: '#2D3748',       // Primary Text
  lightGray: '#E2E8F0',      // Inverse Text (on dark backgrounds)

  // Specific colors for Fix 2 design
  slateBlue: '#4a5568',      // Used for headers and dark backgrounds
  secondary: '#4a5568',      // Same as slateBlue for backward compatibility
  primary: '#4299e1',        // Action Blue
  
  // Item type colors
  itemsColor: '#C05621',     // Brown/orange for item icons
  boxesColor: '#805AD5',     // Purple for box icons
  tagsColor: '#4299e1',      // Blue for tag icons

  // Standard colors
  white: '#ffffff',
  black: '#000000',
  backgroundColor: '#FDFDFD', // Light background color
  
  // Semantic colors
  success: '#38a169',
  danger: '#e53e3e',
  warning: '#f6ad55',
  info: '#4299e1',
  
  // Utility colors
  overlay: 'rgba(0, 0, 0, 0.5)',
  divider: 'rgba(0, 0, 0, 0.12)',
  
  // Gray scale
  gray: {
    100: '#f7fafc',
    200: '#edf2f7',
    300: '#e2e8f0',        // Same as lightGray
    400: '#cbd5e0',
    500: '#a0aec0',
    600: '#718096',
    700: '#4a5568',        // Same as slateBlue
    800: '#2d3748',        // Same as charcoal 
    900: '#1a202c'
  },
};

// Typography settings
export const typography = {
  fontFamily: {
    primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif",
    heading: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  fontSizes: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    base: '1rem',     // 16px
    md: '1.125rem',   // 18px
    lg: '1.25rem',    // 20px
    xl: '1.5rem',     // 24px
    '2xl': '1.75rem', // 28px
    '3xl': '2rem',    // 32px
  },
  fontWeights: {
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
};

// Spacing values for consistent layout
export const spacing = {
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  '2xl': '2.5rem', // 40px
  '3xl': '3rem',   // 48px
};

// Border radius values
export const borderRadius = {
  sm: '0.25rem',  // 4px
  md: '0.5rem',   // 8px
  lg: '0.75rem',  // 12px
  xl: '1rem',     // 16px
  full: '9999px', // Fully rounded
};

// Box shadows
export const shadows = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

// Transitions
export const transitions = {
  default: 'all 0.2s ease-in-out',
  slow: 'all 0.3s ease-in-out',
  fast: 'all 0.1s ease-in-out',
};

// Breakpoints for responsive design
export const breakpoints = {
  xs: '0px',
  sm: '600px',
  md: '960px',
  lg: '1280px',
  xl: '1920px',
};

// Z-index values to manage stacking contexts
export const zIndex = {
  appBar: 1100,
  drawer: 1200,
  modal: 1300,
  snackbar: 1400,
  tooltip: 1500,
};

// Export all style constants as a single object
export const styleConstants = {
  colors,
  typography,
  spacing,
  borderRadius,
  shadows,
  transitions,
  zIndex,
  breakpoints,
};

export default styleConstants;
